import { createFileRoute, Outlet, useParams, useChildMatches } from '@tanstack/react-router'
import { useMutation, useSuspenseQuery, useQueryClient } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { useLingui } from '@lingui/react/macro'
import { Button } from '@/components/ui/button'
import { ExternalLinkIcon, PlusIcon, CodeIcon, BookOpenIcon, Loader2Icon, CheckIcon } from 'lucide-react'
import { trpc } from '@/main'
import * as v from 'valibot'
import { cn } from '@/lib/utils'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// Each `$appId` management section is its own route; the cards below navigate to the matching one.
const SECTION_ROUTES = {
  settings: '/account/api/$appId/settings',
  applicants: '/account/api/$appId/applicants',
  companies: '/account/api/$appId/companies',
  jobs: '/account/api/$appId/jobs',
  playground: '/account/api/$appId/playground',
} as const
type Section = keyof typeof SECTION_ROUTES

export const Route = createFileRoute('/_app/account/api')({
  validateSearch: v.object({
    email: v.optional(v.pipe(v.string(), v.email())),
    p: v.optional(v.number(), 0),
  }),
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.apps.getAll.queryOptions())
  },
  component: RouteComponent,
  pendingComponent: ApiPending,
  staticData: {
    pageTitle: 'API',
    pageIcon: CodeIcon,
  },
})

function ApiPending() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Skeleton className="h-9 w-40 rounded-lg" />
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {['a', 'b', 'c'].map((k) => (
            <Skeleton key={k} className="h-[58px] rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  )
}

function RouteComponent() {
  const { t } = useLingui()
  // Param lives on the `$appId` child route, so read it loosely (undefined when no client is selected).
  const { appId } = useParams({ strict: false })
  const childMatches = useChildMatches()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { data: apps } = useSuspenseQuery(trpc.apps.getAll.queryOptions())
  const { mutateAsync: createApp, isPending: isCreating } = useMutation(trpc.apps.create.mutationOptions())

  // Resolve the active section from the matched leaf route (not by parsing the path), so switching
  // clients keeps you on the same tab. The bare `$appId` index has no section → defaults to settings.
  const activeRouteId = childMatches.at(-1)?.routeId
  const section: Section =
    (Object.keys(SECTION_ROUTES) as Section[]).find((key) => activeRouteId === `/_app${SECTION_ROUTES[key]}`) ??
    'settings'

  const handleCreateApp = async () => {
    const newApp = await createApp()
    await queryClient.invalidateQueries(trpc.apps.getAll.queryFilter())
    navigate({ to: '/account/api/$appId/settings', params: { appId: newApp.clientId } })
  }

  const handleAppSelect = (clientId: string) => {
    navigate({ to: SECTION_ROUTES[section], params: { appId: clientId } })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Documentation link */}
      <div className="flex justify-end">
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-card hover:bg-accent hover:text-accent-foreground inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
        >
          <BookOpenIcon className="size-4" />
          {t`Documentation`}
          <ExternalLinkIcon className="size-3.5" />
        </a>
      </div>

      {/* App Selection */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium">{t`API Clients`}</h2>
            <p className="text-muted-foreground text-xs">{t`Select a client to manage settings and data`}</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleCreateApp} disabled={isCreating}>
            {isCreating ? <Loader2Icon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
            {t`New Client`}
          </Button>
        </div>

        {apps.length === 0 ? (
          <PanelEmptyState
            icon={<CodeIcon className="text-muted-foreground size-5" />}
            title={t`No API clients yet`}
            description={t`Create your first client to start integrating`}
            action={
              <Button type="button" variant="default" size="sm" onClick={handleCreateApp} disabled={isCreating}>
                {isCreating ? <Loader2Icon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
                {t`Create API Client`}
              </Button>
            }
          />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {apps.map((item) => (
              <AppCard
                key={item.clientId}
                app={item}
                isSelected={item.clientId === appId}
                onClick={() => handleAppSelect(item.clientId)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Selected client's tabs (settings/applicants/companies/jobs) — the `$appId` child route. */}
      <Outlet />
    </div>
  )
}

function AppCard(props: { app: { clientId: string; name: string | null }; isSelected: boolean; onClick: () => void }) {
  return (
    <ButtonPrimitive
      type="button"
      onClick={props.onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-200 cursor-pointer',
        props.isSelected ? 'border-primary/30 bg-primary/5' : 'hover:bg-foreground/2.5',
      )}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          props.isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
        )}
      >
        <CodeIcon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{props.app.name || 'Unnamed App'}</p>
        <p className="text-muted-foreground truncate font-mono text-[10px]">{props.app.clientId.slice(0, 16)}...</p>
      </div>
      {props.isSelected && (
        <div className="bg-primary/10 text-primary flex size-5 shrink-0 items-center justify-center rounded-full">
          <CheckIcon className="size-3" />
        </div>
      )}
    </ButtonPrimitive>
  )
}
